import React, { useRef, useState } from "react";
import {
  CategoryItem,
  CategoryText,
  CloseIcon,
  CloseIconWrapper,
  Container,
  FolderItem,
  SearchFilter,
  StyledInput,
} from "./CollectionFilter.styles";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as FavoritesIcon } from "../../assets/icons/favorites.svg";
import { ReactComponent as PlusCircleIcon } from "../../assets/icons/plus-circle.svg";
import { useSelector } from "react-redux";
import { selectCategories } from "../../store/slices/model/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { useSearchParams } from "react-router-dom";

const SKELETON_WIDTHS = [44, 82, 51, 77, 110, 61];
const LONG_PRESS_DURATION = 500;

const CollectionFilter = ({
  onSearchChange,
  filter,
  searchValue,
  setFilter,
  hideFilter,
  hideSearch = false,
  alwaysExpanded,
  searchPlaceholderText,
  openCreateFolderModal,
  folders,
  showFolders,
  openDeleteFolder,
  setSelectedDeleteFolder,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSearchExpanded, setIsSearchExpanded] = useState(
    alwaysExpanded ?? false
  );
  const { data: categories, loading } = useSelector(selectCategories);
  const loggedIn = useSelector(selectLoggedIn);
  const inputRef = useRef();

  const longPressTimer = useRef(null);
  const isLongPress = useRef(false);

  const startLongPress = (folder) => {
    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      setSelectedDeleteFolder(folder);
      openDeleteFolder(folder._id);
    }, LONG_PRESS_DURATION);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleFolderClick = (folder) => {
    if (isLongPress.current) {
      isLongPress.current = false;
      return;
    }
    setFilter(folder._id);
  };

  return (
    <Container>
      {!hideSearch && (
        <SearchFilter
          $expanded={isSearchExpanded}
          $alwaysExpanded={alwaysExpanded}
        >
          <SearchIcon
            style={{
              minWidth: "20px",
              position: "absolute",
              opacity: alwaysExpanded ? 0.33 : 1,
            }}
          />
          <StyledInput
            ref={inputRef}
            placeholder={searchPlaceholderText ?? "title, username or tags"}
            value={searchValue}
            onBlur={() => {
              if (!searchValue && !alwaysExpanded) {
                setIsSearchExpanded(false);
              }
            }}
            onFocus={() => setIsSearchExpanded(true)}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {!alwaysExpanded && (
            <CloseIconWrapper
              onClick={() => {
                if (!isSearchExpanded) {
                  inputRef.current.focus();
                }
                setIsSearchExpanded(!isSearchExpanded);
                onSearchChange("");
              }}
              $expanded={isSearchExpanded}
            >
              {isSearchExpanded && <CloseIcon />}
            </CloseIconWrapper>
          )}
        </SearchFilter>
      )}

      {loggedIn && showFolders && (
        <>
          <CategoryItem
            $selected={filter === "FAVORITES"}
            $width={SKELETON_WIDTHS[0]}
            onClick={() => setFilter("FAVORITES")}
          >
            <FavoritesIcon color={"#fff"} />
          </CategoryItem>

          <CategoryItem
            $width={SKELETON_WIDTHS[0]}
            onClick={openCreateFolderModal}
          >
            <PlusCircleIcon color={"#fff"} />
          </CategoryItem>

          <FolderItem
            $selected={filter === "ALL"}
            $width={SKELETON_WIDTHS[0]}
            onClick={() => setFilter("ALL")}
          >
            <CategoryText>All</CategoryText>
          </FolderItem>

          {folders.map((folder, i) => (
            <FolderItem
              key={folder._id || i}
              $selected={filter === folder._id}
              $width={SKELETON_WIDTHS[0]}
              onClick={() => handleFolderClick(folder)}
              onMouseDown={() => startLongPress(folder)}
              onMouseUp={cancelLongPress}
              onMouseLeave={cancelLongPress}
              onTouchStart={() => startLongPress(folder)}
              onTouchEnd={cancelLongPress}
              onTouchMove={cancelLongPress}
            >
              <CategoryText>{folder.name}</CategoryText>
            </FolderItem>
          ))}
        </>
      )}

      {!hideFilter &&
        (loading ? (
          Array.from({ length: loggedIn ? 6 : 5 }).map((_, index) => (
            <CategoryItem
              key={index}
              $width={SKELETON_WIDTHS[loggedIn ? index : index + 1]}
              $animation
            />
          ))
        ) : (
          <>
            {loggedIn && (
              <CategoryItem
                $selected={filter === "FAVORITES"}
                $width={SKELETON_WIDTHS[0]}
                onClick={() => {
                  searchParams.delete("page");
                  setSearchParams(searchParams);
                  setFilter("FAVORITES");
                }}
              >
                <FavoritesIcon color={"#fff"} />
              </CategoryItem>
            )}
            {!!categories &&
              categories.map((category, index) => (
                <CategoryItem
                  $selected={filter === category._id}
                  key={category._id}
                  $width={SKELETON_WIDTHS[index + 1]}
                  onClick={() => {
                    searchParams.delete("page");
                    setSearchParams(searchParams);
                    setFilter(category._id);
                  }}
                >
                  <CategoryText $selected={filter === category._id}>
                    {category.name}
                  </CategoryText>
                </CategoryItem>
              ))}
          </>
        ))}
    </Container>
  );
};

export default CollectionFilter;
